"use client";
import Icon from "@/components/icons/Icons";
import InformationButton from "@/components/ui/InformationButton";
import { CompareApiResponse, PortfolioApiItem } from "@/type/comparePageTypes";
import { buildCompareTableData } from "@/utils/comparePageFunctions";
import { useIsMobile } from "@/utils/useIsMobile";
import { useLoginModal } from "@/utils/useLoginModal";
import useScrollDirection from "@/utils/useScrollDirection";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useRef, useState } from "react";
import DownloadCompareReport from "../ui/DownloadCompareReport";

type Props = {
  compareFundData: CompareApiResponse;
  portfolioDataArray: PortfolioApiItem[];
};

const getGridTemplate = (count: number): string => `minmax(0, 160px) repeat(${count}, minmax(180px, 1fr))`;

export default function CompareTable({ compareFundData, portfolioDataArray }: Props) {
  const { isLoggedIn, open, setModalKey } = useLoginModal();
  const tableAllData = buildCompareTableData(compareFundData, portfolioDataArray, isLoggedIn);

  const columns = tableAllData.columns;
  const sections = tableAllData.sections;

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    const ids = sections.map((s) => s.id);
    return new Set(ids.slice(0, -2));
  });

  const scrollDirection = useScrollDirection(20);
  const showNav = scrollDirection !== "down";
  const isMobile = useIsMobile(991);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const isSyncingRef = useRef<"header" | "body" | null>(null);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  const onHeaderScroll = () => {
    if (isSyncingRef.current === "body") {
      isSyncingRef.current = null;
      return;
    }

    if (headerRef.current && bodyRef.current) {
      isSyncingRef.current = "header";
      bodyRef.current.scrollLeft = headerRef.current.scrollLeft;
    }
  };

  const onBodyScroll = () => {
    if (isSyncingRef.current === "header") {
      isSyncingRef.current = null;
      return;
    }

    if (headerRef.current && bodyRef.current) {
      isSyncingRef.current = "body";
      headerRef.current.scrollLeft = bodyRef.current.scrollLeft;
    }
  };

  const gridTemplate = getGridTemplate(columns.length);

  const maxWidth = columns.length === 4 ? "260px" : columns.length === 3 ? "348px" : columns.length === 2 ? "522px" : undefined;

  return (
    <>
      <DownloadCompareReport compareFundData={compareFundData} portfolioDataArray={portfolioDataArray} />

      <div className="relative max-w-section">
        <div className={`sticky z-3 transition-all duration-400 ease-in-out ${isMobile ? (showNav ? "top-17.5 sm:top-18.75" : "top-0") : "top-18.75"}`}>
          <div ref={headerRef} onScroll={onHeaderScroll} className="overflow-x-auto scrollbar-none sm:rounded-t-4xl bg-slate-50 text-sm leading-1.2 font-bold">
            <div className="min-w-245 grid shadow-xs" style={{ gridTemplateColumns: gridTemplate }}>
              <div className="max-w-40 sticky flex items-center border border-slate-200 sm:rounded-tl-4xl left-0 z-1 bg-slate-50 p-3 sm:p-4 text-[#90A1B9]">
                <span>METRICS</span>
              </div>

              {columns.map((col) => (
                <div key={col.id} className={`flex items-center justify-center w-full p-3 sm:p-4 border border-l-0 border-slate-200 sm:last-of-type:rounded-tr-4xl text-primaryBlack`} style={{ maxWidth }}>
                  <span className="line-clamp-2 text-center">{col.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div ref={bodyRef} onScroll={onBodyScroll} className="overflow-x-auto scrollbar-none">
          <div className="min-w-245">
            {/* BODY */}
            {sections.map((section) => {
              const isOpen = openSections.has(section.id);

              return (
                <Fragment key={section.id}>
                  {/* SECTION HEADER */}
                  <div data-section-toggle onClick={() => toggleSection(section.id)} className="flex items-center gap-2 bg-slate-100 px-4 py-3 border-b border-[#e4e6e9] text-sm font-bold text-[#1D293D] cursor-pointer sm:last-of-type:rounded-b-4xl uppercase">
                    <div className="sticky left-4 inline-flex items-center gap-2">
                      {section.title}

                      {section.descriptionKey && <InformationButton keywords={section.descriptionKey} className="w-4.5! h-4.5! text-[#444444]! hover:text-primaryBlue!" />}
                    </div>
                    <Icon.expandableIcon isOpen={isOpen} className="sticky right-4 ml-auto" />
                  </div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35, ease: "easeInOut" }}>
                        {section.rows.map((row, rowIndex) => (
                          <div key={`${section.id}-${rowIndex}`} className="grid border-b last:border-none sm:last-of-type:rounded-b-4xl" style={{ gridTemplateColumns: gridTemplate }}>
                            {/* STICKY FIRST COLUMN */}
                            <div className="max-w-40 sticky flex items-center gap-2 left-0 z-2 border border-slate-100 bg-[#fafbfe] p-3 sm:p-4 text-sm font-semibold text-[#45556C]">
                              <span>{row.label}</span>
                              {row.descriptionKey && <InformationButton keywords={row.descriptionKey} className="w-4.5! h-4.5! text-[#444444]!" />}
                            </div>

                            {row.values.map((value, idx) => (
                              <div key={idx} className={`p-3 sm:p-4 w-full flex items-center justify-center text-center text-sm border border-l-0 border-slate-100 text-[#314158] relative ${row.highlightIndex === idx ? "bg-green-50 text-green-700 font-semibold" : ""}`} style={{ maxWidth }}>
                                {row.id === "exit-load" ? (
                                  <span title={value}>{value}</span>
                                ) : (
                                  <span className={`line-clamp-2 ${row.isLocked ? "blur-xs" : ""}`} title={value}>
                                    {value}
                                  </span>
                                )}

                                {row.isLocked && (
                                  <button
                                    onClick={() => {
                                      open();
                                      setModalKey("compare-page-table-info");
                                    }}
                                    className="group absolute inset-0 flex items-center justify-center"
                                  >
                                    <Icon.lockIconV3 className="absolute m-auto text-[#3b3b3b] inset-0 w-5 h-5 transition-all duration-300 ease-out opacity-100 scale-100 group-hover:opacity-0 group-hover:scale-90" />

                                    <span className="absolute w-fit inset-x-0 mx-auto flex items-center justify-center gap-2 sm:rounded-xl py-1 px-2 bg-[#0E2332] font-semibold text-xs text-white transition-all duration-300 ease-out opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100">
                                      <PremiumIcon />
                                      Unlock Data
                                    </span>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}

const PremiumIcon = () => {
  return (
    <svg width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.1514 10.7412L14.0889 10.9336L11.1416 19.9375L11.002 20.3652L10.8613 19.9375L7.91504 10.9336L7.85156 10.7412H14.1514ZM21.458 10.7412L21.3516 10.9541C21.2474 11.1628 21.1081 11.3577 20.9365 11.5293L12.5928 19.874L12.3486 19.7236L15.2559 10.8418L15.2891 10.7412L21.458 10.7412ZM6.71387 10.7412L6.74707 10.8418L9.6543 19.7227L9.41016 19.873L1.06641 11.5293C0.894714 11.3576 0.756401 11.1626 0.652344 10.9541L0.545898 10.7412H6.71387ZM8.04883 3.40723L8.00879 3.58594L6.69727 9.54492L6.6709 9.66016H0.433594L0.473633 9.48047C0.519819 9.27429 0.596109 9.07183 0.704102 8.88086L3.1582 4.53125C3.35064 4.19017 3.63056 3.90646 3.96875 3.70898C4.30694 3.51153 4.69133 3.40717 5.08301 3.40723H8.04883ZM12.8477 3.40723L12.873 3.52344L14.1855 9.48145L14.2246 9.66016H7.77832L7.81738 9.48145L9.12988 3.52344L9.15527 3.40723H12.8477ZM16.9209 3.40723C17.3125 3.40717 17.697 3.5116 18.0352 3.70898C18.3733 3.90644 18.6533 4.19023 18.8457 4.53125L21.2998 8.88086C21.4075 9.07186 21.484 9.27344 21.5303 9.48047L21.5703 9.66016L15.3311 9.66016L15.3057 9.54492L13.9941 3.58594L13.9541 3.40723L16.9209 3.40723Z"
        fill="url(#paint0_linear_5856_25218)"
        stroke="white"
        strokeWidth="0.294403"
      />
      <g filter="url(#filter0_i_5856_25218)">
        <path
          d="M15.1458 6.81877L18.342 6.84909C18.3802 6.84982 18.4176 6.86024 18.4508 6.87936C18.4839 6.89849 18.5116 6.92571 18.5314 6.95847L20.1557 9.71128C20.1807 9.75293 20.2186 9.78531 20.2636 9.80352C20.3086 9.82173 20.3583 9.82479 20.4052 9.81222C20.4521 9.79965 20.4937 9.77215 20.5235 9.73387C20.5534 9.69559 20.57 9.64862 20.5708 9.60007L20.6011 6.40389C20.6018 6.36564 20.6123 6.3282 20.6314 6.29507C20.6505 6.26194 20.6777 6.2342 20.7105 6.21444L23.4633 4.5901C23.5049 4.56513 23.5373 4.52728 23.5555 4.48227C23.5738 4.43725 23.5768 4.38753 23.5642 4.34063C23.5517 4.29373 23.5242 4.2522 23.4859 4.22232C23.4476 4.19245 23.4006 4.17585 23.3521 4.17505L20.1559 4.14474C20.1177 4.14401 20.0802 4.13359 20.0471 4.11447C20.014 4.09534 19.9862 4.06812 19.9665 4.03536L18.3421 1.28255C18.3172 1.2409 18.2793 1.20852 18.2343 1.19031C18.1893 1.17209 18.1396 1.16904 18.0927 1.18161C18.0457 1.19418 18.0042 1.22168 17.9743 1.25996C17.9445 1.29824 17.9279 1.34521 17.9271 1.39376L17.8968 4.58994C17.896 4.62819 17.8856 4.66563 17.8665 4.69876C17.8474 4.73189 17.8201 4.75963 17.7874 4.77939L15.0346 6.40373C14.9929 6.4287 14.9605 6.46655 14.9423 6.51156C14.9241 6.55657 14.9211 6.60629 14.9336 6.6532C14.9462 6.7001 14.9737 6.74163 15.012 6.77151C15.0503 6.80138 15.0972 6.81798 15.1458 6.81877Z"
          fill="url(#paint1_linear_5856_25218)"
        />
      </g>
      <path
        d="M15.1458 6.81877L18.342 6.84909C18.3802 6.84982 18.4176 6.86024 18.4508 6.87936C18.4839 6.89849 18.5116 6.92571 18.5314 6.95847L20.1557 9.71128C20.1807 9.75293 20.2186 9.78531 20.2636 9.80352C20.3086 9.82173 20.3583 9.82479 20.4052 9.81222C20.4521 9.79965 20.4937 9.77215 20.5235 9.73387C20.5534 9.69559 20.57 9.64862 20.5708 9.60007L20.6011 6.40389C20.6018 6.36564 20.6123 6.3282 20.6314 6.29507C20.6505 6.26194 20.6777 6.2342 20.7105 6.21444L23.4633 4.5901C23.5049 4.56513 23.5373 4.52728 23.5555 4.48227C23.5738 4.43725 23.5768 4.38753 23.5642 4.34063C23.5517 4.29373 23.5242 4.2522 23.4859 4.22232C23.4476 4.19245 23.4006 4.17585 23.3521 4.17505L20.1559 4.14474C20.1177 4.14401 20.0802 4.13359 20.0471 4.11447C20.014 4.09534 19.9862 4.06812 19.9665 4.03536L18.3421 1.28255C18.3172 1.2409 18.2793 1.20852 18.2343 1.19031C18.1893 1.17209 18.1396 1.16904 18.0927 1.18161C18.0457 1.19418 18.0042 1.22168 17.9743 1.25996C17.9445 1.29824 17.9279 1.34521 17.9271 1.39376L17.8968 4.58994C17.896 4.62819 17.8856 4.66563 17.8665 4.69876C17.8474 4.73189 17.8201 4.75963 17.7874 4.77939L15.0346 6.40373C14.9929 6.4287 14.9605 6.46655 14.9423 6.51156C14.9241 6.55657 14.9211 6.60629 14.9336 6.6532C14.9462 6.7001 14.9737 6.74163 15.012 6.77151C15.0503 6.80138 15.0972 6.81798 15.1458 6.81877Z"
        stroke="#FCD34D"
        strokeWidth="0.0490671"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <filter id="filter0_i_5856_25218" x="14.9023" y="0.756877" width="8.69531" height="9.08785" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
          <feOffset dy="-0.392537" />
          <feGaussianBlur stdDeviation="0.647686" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.68 0" />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow_5856_25218" />
        </filter>
        <linearGradient id="paint0_linear_5856_25218" x1="-0.248047" y1="5.28418" x2="13.752" y2="18.0342" gradientUnits="userSpaceOnUse">
          <stop stopColor="#DDE6F5" />
          <stop offset="1" stopColor="#00358B" />
        </linearGradient>
        <linearGradient id="paint1_linear_5856_25218" x1="16.4905" y1="2.73955" x2="22.0074" y2="8.25428" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FCD966" />
          <stop offset="0.5" stopColor="#FCD966" />
          <stop offset="1" stopColor="#FCCD34" />
        </linearGradient>
      </defs>
    </svg>
  );
};
